import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  IconButton, 
  Grid, 
  Button, 
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { v4 as uuidv4 } from 'uuid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import UploadIcon from '@mui/icons-material/Upload';
import ShareDialog from './ShareDialog';
import ImageCompare from './ImageCompare';
import { supabase } from '../supabaseClient';

const StyledImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
  transition: 'all 0.3s ease-in-out',
  opacity: 1,
  '&:hover': {
    filter: 'brightness(1.1) contrast(1.1)',
  },
}));

const PropertyDetail = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [session, setSession] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newImageName, setNewImageName] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const fetchPropertyData = useCallback(async () => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/signup', {
          state: { 
            returnUrl: `/property/${propertyId}`,
            message: 'Mülk detaylarını görüntülemek için lütfen giriş yapın veya kaydolun'
          }
        });
        return;
      }

      // Get images for this property
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('property_id', propertyId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get property info directly from properties table
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', propertyId)
        .single();
        
      if (propertyError && propertyError.code !== 'PGRST116') {
        // PGRST116 is the error code for "no rows returned"
        throw propertyError;
      }
      
      if (propertyData) {
        setProperty({
          id: propertyId,
          name: propertyData.name || 'Unnamed Property',
          createdAt: propertyData.created_at
        });
        
        // Set images if we have any
        setImages(data || []);
      } else if (data && data.length > 0) {
        // Fallback: Set property info from the first image if we couldn't get it directly
        setProperty({
          id: propertyId,
          name: data[0].property_name || 'Unnamed Property',
          createdAt: data[0].created_at
        });
        
        setImages(data);
      } else {
        // No property or images found
        console.log('No property found with ID:', propertyId);
        navigate('/properties');
      }
    } catch (error) {
      console.error('Error fetching property data:', error);
    } finally {
      setLoading(false);
    }
  }, [propertyId, navigate]);

  useEffect(() => {
    fetchPropertyData();
    
    // Get current user session
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    getSession();
  }, [fetchPropertyData]);

  const handleMenuOpen = (event, image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowCompare(true);
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedImage) return;

    try {
      // Delete the image
      const { error } = await supabase
        .from('images')
        .delete()
        .eq('id', selectedImage.id);

      if (error) throw error;
      
      // Update local state
      setImages(prev => prev.filter(img => img.id !== selectedImage.id));
      
      // If no images left, navigate back to properties
      if (images.length <= 1) {
        navigate('/properties');
      }
      
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleRenameClick = () => {
    setNewImageName(selectedImage?.prompt || '');
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    if (!newImageName.trim() || !selectedImage) return;

    try {
      // Update image prompt instead of name
      const { error } = await supabase
        .from('images')
        .update({ prompt: newImageName })
        .eq('id', selectedImage.id);

      if (error) throw error;
      
      // Update local state
      setImages(prev => 
        prev.map(img => img.id === selectedImage.id 
          ? { ...img, prompt: newImageName } 
          : img
        )
      );
      
      setRenameDialogOpen(false);
    } catch (error) {
      console.error('Error renaming image:', error);
    }
  };

  const handleAddImages = () => {
    navigate('/canvas-editor', { state: { propertyId } });
  };
  
  const handleUploadImages = async () => {
    if (uploadedFiles.length === 0) return;
    
    try {
      setUploading(true);
      setUploadError(null);
      setUploadProgress(0);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Kullanıcı oturumu bulunamadı');
      
      // Process each file
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        
        // Update progress
        setUploadProgress((i / uploadedFiles.length) * 100);
        
        // Generate a unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;
        
        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from('uploaded-images')
          .upload(filePath, file);
          
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: publicUrlData } = supabase.storage
          .from('uploaded-images')
          .getPublicUrl(filePath);
          
        const publicUrl = publicUrlData?.publicUrl;
        if (!publicUrl) throw new Error('Dosya URL\'i alınamadı');
        
        // Create image record in database
        const imageId = uuidv4();
        const imageData = {
          id: imageId,
          user_id: user.id,
          url: publicUrl,
          prompt: file.name.split('.')[0], // Use filename as prompt instead of name
          property_id: propertyId,
          property_name: property?.name || 'Unnamed Property',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { error: dbError } = await supabase
          .from('images')
          .insert(imageData);
          
        if (dbError) throw dbError;
      }
      
      // Final progress update
      setUploadProgress(100);
      
      // Refresh images
      fetchPropertyData();
      
      // Close dialog after a short delay
      setTimeout(() => {
        setUploadDialogOpen(false);
        setUploadedFiles([]);
        setUploading(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setUploadError(`Yükleme hatası: ${error.message}`);
      setUploading(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '1600px', 
      margin: '0 auto', 
      padding: '30px 20px',
      position: 'relative',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      borderRadius: '24px',
      boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
      border: theme => `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {property?.name || 'Mülk Detayı'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {property && `${images.length} Görsel - Güncellendi ${new Date(property.createdAt).toLocaleDateString()}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={() => setShareDialogOpen(true)}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.04)',
                borderColor: 'primary.dark',
              }
            }}
          >
            Paylaş
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(138, 43, 226, 0.04)',
                borderColor: 'primary.dark',
              }
            }}
          >
            Yükle
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleAddImages}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            Görsel Ekle
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : images.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: 300,
          textAlign: 'center',
          p: 3
        }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Bu mülkte henüz görsel bulunmuyor
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Yeni bir görsel eklemek için "Görsel Ekle" butonuna tıklayın
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleAddImages}
          >
            Görsel Ekle
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Box
                sx={{
                  position: 'relative',
                  paddingTop: '75%',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  borderRadius: '12px',
                  boxShadow: theme => `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
                  transition: 'all 0.3s ease',
                  backgroundColor: 'background.paper',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme => `0 8px 24px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.2)'}`,
                  },
                }}
                onClick={() => handleImageClick(image)}
              >
                <StyledImage
                  src={image.url}
                  alt={image.name || `Image ${image.id}`}
                  loading="lazy"
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px 12px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 'calc(100% - 40px)'
                  }}>
                    {image.prompt?.substring(0, 30) || 'Render'}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleMenuOpen(e, image);
                    }}
                    className="menu-button"
                    sx={{
                      color: 'white',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      zIndex: 10
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Image Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              borderRadius: '8px',
              mb: 0.5,
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          handleImageClick(selectedImage);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Görüntüle</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          navigate(`/editor/${selectedImage.id}`);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          navigate(`/canvas-editor`, { 
            state: { 
              editImage: selectedImage,
              propertyId
            } 
          });
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          // Handle download image
          if (selectedImage?.url) {
            // Create a temporary anchor element
            const link = document.createElement('a');
            
            // Fetch the image first to handle CORS issues
            fetch(selectedImage.url)
              .then(response => response.blob())
              .then(blob => {
                // Create a blob URL for the image
                const blobUrl = URL.createObjectURL(blob);
                
                // Set up the download
                link.href = blobUrl;
                link.download = `${selectedImage.prompt || 'image'}.png`;
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                setTimeout(() => {
                  document.body.removeChild(link);
                  URL.revokeObjectURL(blobUrl);
                }, 100);
              })
              .catch(error => {
                console.error('Error downloading image:', error);
                // Fallback to opening in new tab
                window.open(selectedImage.url, '_blank');
              });
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>İndir</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sil</ListItemText>
        </MenuItem>
      </Menu>

      {/* Image Viewer Modal */}
      <Dialog
        open={showCompare}
        onClose={() => setShowCompare(false)}
        maxWidth="xl"
        fullWidth
      >
        {selectedImage && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setShowCompare(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                zIndex: 1,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.name || 'Image'}
              style={{ 
                width: '100%', 
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Görseli Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu görseli silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Görseli Yeniden Adlandır</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Görsel Adı"
            type="text"
            fullWidth
            value={newImageName}
            onChange={(e) => setNewImageName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>İptal</Button>
          <Button onClick={handleRenameConfirm} variant="contained">Kaydet</Button>
        </DialogActions>
      </Dialog>
      
      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        itemType="property"
        itemId={propertyId}
        itemName={property?.name || 'Mülk'}
        thumbnailUrl={property?.thumbnail_url || (images.length > 0 ? images[0].url : null)}
        userId={session?.user?.id}
        includeAllImages={true}
      />
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Görsel Yükle</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}
            
            <Box 
              sx={{ 
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: '12px',
                p: 3,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(138, 43, 226, 0.04)',
                }
              }}
              onClick={() => document.getElementById('file-upload').click()}
            >
              <input
                id="file-upload"
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setUploadedFiles(files);
                }}
              />
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Görselleri buraya sürükleyin veya tıklayın
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PNG, JPG, WEBP formatları desteklenir (max 10MB)
              </Typography>
            </Box>
            
            {uploadedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Yüklenecek Görseller ({uploadedFiles.length})
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {uploadedFiles.map((file, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        width: 100, 
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Preview ${index}`}
                        style={{ width: '100%', height: 100, objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            
            {uploading && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Yükleniyor... {Math.round(uploadProgress)}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={() => setUploadDialogOpen(false)}
            disabled={uploading}
          >
            İptal
          </Button>
          <Button 
            variant="contained"
            onClick={handleUploadImages}
            disabled={uploading || uploadedFiles.length === 0}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Yükleniyor...' : 'Yükle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyDetail;
